import {
  interpolateColor,
  useAnimatedReaction,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { useExtractedQuestionsBubbleContext } from '@/components/chat/bubbles/extractedQuestions/ExtractedQuestionsBubbleContext';

interface PulseOptions {
  baseColor: string;
  pulseColor: string;
  rampUpMs?: number;
  rampDownMs?: number;
}

/**
 * Animated background-color pulse for the matching question card.
 * Subscribes via `useAnimatedReaction` to the per-question pulse tick
 * SharedValue owned by the bubble's wiring hook. The pulse trigger never
 * goes through React — `pulseQuestion` mutates the SharedValue
 * directly and the reaction worklet runs the timing sequence on the UI
 * thread.
 *
 * Honors reduced-motion: keeps progress at 0 instead of running the
 * timing sequence.
 */
export function useQuestionPulseStyle(questionIndex: number, options: PulseOptions) {
  const { getPulseTick } = useExtractedQuestionsBubbleContext();

  const tick = getPulseTick(questionIndex);

  const reducedMotion = useReducedMotion();

  const progress = useSharedValue(0);

  const { baseColor, pulseColor, rampUpMs = 150, rampDownMs = 450 } = options;

  useAnimatedReaction(
    () => tick.value,
    (current, previous) => {
      if (previous === null || current === previous) {
        return;
      }

      if (reducedMotion) {
        progress.value = 0;

        return;
      }

      progress.value = withSequence(
        withTiming(1, { duration: rampUpMs }),
        withTiming(0, { duration: rampDownMs })
      );
    },
    // reducedMotion is included so the worklet closure re-registers and
    // captures the updated value when the system preference changes.
    [reducedMotion, rampUpMs, rampDownMs]
  );

  return useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(progress.value, [0, 1], [baseColor, pulseColor]),
  }));
}
